import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Nachricht gesendet!",
          description: "Vielen Dank für Ihre Anfrage. Wir melden uns in Kürze bei Ihnen.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Fehler",
        description: error.response?.data?.detail || "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" style={styles.section}>
      <div className="container">
        <div style={styles.content}>
          <div style={styles.textContent}>
            <h2 className="heading-1" style={styles.title}>Kontaktieren Sie uns</h2>
            <p className="body-large" style={styles.description}>
              Haben Sie Fragen zum Coaching oder möchten Sie mehr erfahren? 
              Schreiben Sie uns eine Nachricht und wir melden uns zeitnah bei Ihnen.
            </p>
            
            <div style={styles.infoBox}>
              <h3 className="heading-3" style={styles.infoTitle}>WAHR Coaching</h3>
              <p className="body-medium" style={styles.infoText}>
                Nicolas Alexander Wahr
              </p>
              <p className="body-medium" style={styles.infoText}>
                Privatinstitut für Dialog & Markenkompetenz
              </p>
              <p className="body-medium" style={styles.infoText}>
                Mittelstraße 11, 40789 Monheim am Rhein
              </p>
              <p className="body-medium" style={styles.infoText}>
                <a href="tel:+491746429886" style={{color: 'var(--brand-primary)', textDecoration: 'none'}}>
                  +49 174 6429886
                </a>
              </p>
              <p className="body-medium" style={styles.infoText}>
                <a href="mailto:Nicolas.wahr@icloud.com" style={{color: 'var(--brand-primary)', textDecoration: 'none'}}>
                  Nicolas.wahr@icloud.com
                </a>
              </p>
            </div>
          </div>
          
          <div style={styles.formWrapper}>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label htmlFor="name" style={styles.label}>Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  placeholder="Ihr vollständiger Name"
                />
              </div>
              
              <div style={styles.formGroup}>
                <label htmlFor="email" style={styles.label}>E-Mail *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  placeholder="ihre.email@beispiel.de"
                />
              </div>
              
              <div style={styles.formGroup}>
                <label htmlFor="phone" style={styles.label}>Telefon</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="+49 123 456789"
                />
              </div>
              
              <div style={styles.formGroup}>
                <label htmlFor="message" style={styles.label}>Nachricht *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  style={styles.textarea}
                  placeholder="Erzählen Sie uns von Ihren Zielen und Herausforderungen..."
                />
              </div>
              
              <button type="submit" className="btn-primary" style={styles.submitButton} disabled={isSubmitting}>
                <Send size={20} style={{ marginRight: '8px' }} />
                {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '80px 0',
    background: 'var(--bg-subtle)',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '48px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  textContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  title: {
    marginBottom: '8px',
  },
  description: {
    color: 'var(--text-secondary)',
  },
  infoBox: {
    background: 'var(--bg-card)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 2px 8px rgba(28, 46, 82, 0.1)',
  },
  infoTitle: {
    marginBottom: '16px',
    color: 'var(--brand-primary)',
  },
  infoText: {
    color: 'var(--text-secondary)',
    marginBottom: '8px',
  },
  formWrapper: {
    background: 'var(--bg-card)',
    borderRadius: '32px',
    padding: '40px',
    boxShadow: '0 4px 16px rgba(28, 46, 82, 0.1)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  input: {
    padding: '12px 16px',
    borderRadius: '12px',
    border: '2px solid var(--border-light)',
    fontSize: '16px',
    transition: 'border-color 0.2s ease',
    fontFamily: 'inherit',
  },
  textarea: {
    padding: '12px 16px',
    borderRadius: '12px',
    border: '2px solid var(--border-light)',
    fontSize: '16px',
    transition: 'border-color 0.2s ease',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '8px',
  },
};

// Add focus styles
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  input:focus, textarea:focus {
    outline: none;
    border-color: var(--brand-primary) !important;
  }
  
  @media (max-width: 781px) {
    #contact > div > div {
      grid-template-columns: 1fr !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default Contact;